import { useState, useEffect, useRef } from 'react'
import { RecaptchaVerifier, type ApplicationVerifier } from 'firebase/auth'
import { ANALYTICS_TAGS } from '../../Constant'
import {
  createDeferred,
  Deferred,
  waitForElement,
  configureFirebaseForDevelopment,
  VERIFIER_CACHE,
  getOrCreateVerifier,
  handleAuthError,
  logEvent,
} from '../../Utils'

export const useRecaptcha = (
  containerId: string,
  shouldInitialize = true,
  onSolved?: () => void
) => {
  const [ready, setReady] = useState(false)
  const verifierRef = useRef<RecaptchaVerifier | null>(null)
  const initOnce = useRef(false)
  const deferredRef = useRef<Deferred<ApplicationVerifier> | null>(null)
  if (!deferredRef.current) deferredRef.current = createDeferred<ApplicationVerifier>()

  useEffect(() => {
    let cancelled = false
    if (!shouldInitialize || initOnce.current) return

    const init = async () => {
      await configureFirebaseForDevelopment()
      if (VERIFIER_CACHE.has(containerId)) {
        const existing = VERIFIER_CACHE.get(containerId)!
        verifierRef.current = existing
        setReady(true)
        deferredRef.current!.resolve(existing)
        return
      }
      initOnce.current = true

      try {
        const el = await waitForElement(containerId, 5000)
        if (!el) {
          throw new Error(`reCAPTCHA container "${containerId}" not found`)
        }

        const verifier = await getOrCreateVerifier(containerId, onSolved)

        if ((verifier as any).widgetId == null) {
          await verifier.render()
        }

        if (!cancelled) {
          verifierRef.current = verifier
          setReady(true)
          deferredRef.current!.resolve(verifier)
        }
      } catch (err) {
        if (!cancelled) {
          handleAuthError(ANALYTICS_TAGS.auth_change_error, err, 'useRecaptcha_init')
          logEvent(ANALYTICS_TAGS.recaptcha_init_failed, {
            element: containerId,
            product: 'web',
          })
          deferredRef.current!.reject(err)
        }
      }
    }

    void init()
    return () => {
      cancelled = true
    }
  }, [containerId, shouldInitialize, onSolved])

  const waitForVerifier = (): Promise<ApplicationVerifier> => {
    if (!verifierRef.current && VERIFIER_CACHE.has(containerId)) {
      const cachedVerifier = VERIFIER_CACHE.get(containerId)!
      verifierRef.current = cachedVerifier
      return Promise.resolve(cachedVerifier)
    }

    return verifierRef.current
      ? Promise.resolve(verifierRef.current)
      : deferredRef.current!.promise
  }

  const ensureRendered = async () => {
    const verifier = (await waitForVerifier()) as any
    if (verifier.widgetId == null) await verifier.render()
  }

  return { ready, waitForVerifier, ensureRendered }
}
